import { FC } from 'react';
import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Grid,
  GridItem,
  Spacer,
} from '@chakra-ui/react';
import { ERROR_CODES } from '@shinetools/errors';
import { printFormat as formatIban } from 'iban';

import { formatAmount } from 'common/formatAmount';
import SunshineIcon from 'components/_core/SunshineIcon';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';

import { Transfer } from '../../../../types';
import RecipientNameDiffersWarningBox from '../RecipientNameDiffersWarningBox';
import TransferField from '../TransferField';
import locales from './locales';

interface TransfersListItemProps {
  transfer: Transfer;
}

const TransfersListItem: FC<TransfersListItemProps> = ({ transfer }) => {
  const hasRecipientNameWarning = transfer.errors.some(
    ({ code }) =>
      code === ERROR_CODES.BULK_BANK_TRANSFER_RECIPIENT_NAME_DIFFERS,
  );

  return (
    <AccordionItem
      _first={{
        borderTopWidth: '0px',
      }}
    >
      {({ isExpanded }) => (
        <>
          <h2>
            <AccordionButton
              _expanded={{ borderBottomWidth: '1px' }}
              _hover={{ bg: 'grey.200' }}
              paddingBottom="space-16"
              paddingLeft="space-24"
              paddingRight={22}
              paddingTop="space-16"
            >
              <Flex flex="1">
                <Text fontWeight="weight-500" variant="primary">
                  {transfer.bankTransferRecipient.name}
                </Text>
                {transfer.bankTransferRecipient.bankTransferRecipientId ===
                null ? (
                  <Box marginLeft="space-16">
                    <SunshineTag colorScheme="green" size="sm">
                      {locales.new}
                    </SunshineTag>
                  </Box>
                ) : null}
                <Spacer />
                {hasRecipientNameWarning ? (
                  <Flex
                    alignItems="center"
                    bg="orange.4"
                    borderRadius="250px"
                    justifyContent="center"
                    marginLeft="space-8"
                    marginRight="space-16"
                    px="space-8"
                    py="space-4"
                  >
                    <SunshineIcon
                      color="orange.-2"
                      name="warning"
                      size="icon-13"
                    />
                  </Flex>
                ) : null}
                <Box marginLeft="auto" marginRight={22}>
                  <Text variant="primary">
                    {formatAmount({
                      forcePlusSign: false,
                      value: transfer.amount,
                    })}
                  </Text>
                </Box>
              </Flex>
              <SunshineIcon
                name="chevron-down"
                size="icon-24"
                transform={isExpanded ? 'rotate(180deg)' : ''}
                transition="all .25s ease-out"
              />
            </AccordionButton>
          </h2>
          <AccordionPanel px="space-24" py="space-32">
            {hasRecipientNameWarning ? (
              <Box marginBottom="space-24">
                <RecipientNameDiffersWarningBox
                  bankTransferRecipientId={
                    transfer.bankTransferRecipient.bankTransferRecipientId
                  }
                />
              </Box>
            ) : null}
            <Grid
              gap="space-24"
              templateColumns="repeat(3, 1fr)"
              templateRows="repeat(3, 1fr)"
            >
              <GridItem colSpan={2} rowSpan={1}>
                <TransferField
                  fieldName={locales.iban}
                  value={formatIban(transfer.bankTransferRecipient.iban)}
                />
              </GridItem>
              <GridItem colSpan={1} rowSpan={1}>
                <TransferField
                  fieldName={locales.bic}
                  value={transfer.bankTransferRecipient.swiftBic}
                />
              </GridItem>
              <GridItem colSpan={3} rowSpan={1}>
                <TransferField
                  fieldName={locales.amount}
                  value={formatAmount({
                    currency: transfer.currency,
                    forcePlusSign: false,
                    value: transfer.amount,
                  })}
                />
              </GridItem>
              <GridItem colSpan={3} rowSpan={1}>
                <TransferField
                  fieldName={locales.userLabel}
                  value={transfer.userLabel ?? undefined}
                />
              </GridItem>
            </Grid>
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
};

export default TransfersListItem;
